import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, Button, Badge } from '@agion/ui'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import type { ChartDeviation } from './infra_chart.service'

interface Props {
  deviations: ChartDeviation[]
  onRefresh: () => void
  isRefreshing: boolean
}

export function ChartDeviationsBanner({ deviations, onRefresh, isRefreshing }: Props) {
  const { t } = useTranslation(['pages'])

  if (deviations.length === 0) return null

  const outdatedCount = deviations.filter(d => d.deviationType === 'outdated').length
  const unknownCount = deviations.filter(d => d.deviationType === 'unknown_version').length

  return (
    <Alert variant="warning">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} />
          <AlertDescription>
            {t('pages:charts.deviations.summary', {
              count: deviations.length,
              defaultValue: '{{count}} chart deviation(s) detected',
            })}
            {outdatedCount > 0 && (
              <Badge variant="warning" className="ml-2">
                {t('pages:charts.deviations.outdated', {
                  count: outdatedCount,
                  defaultValue: '{{count}} outdated',
                })}
              </Badge>
            )}
            {unknownCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {t('pages:charts.deviations.unknown', {
                  count: unknownCount,
                  defaultValue: '{{count}} unknown version',
                })}
              </Badge>
            )}
          </AlertDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          <RefreshCw size={14} className={isRefreshing ? 'animate-spin mr-1' : 'mr-1'} />
          {t('pages:charts.deviations.refresh', 'Refresh')}
        </Button>
      </div>
    </Alert>
  )
}
